import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faShareNodes,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

type Midia = {
  CdMidia: number
  PublicUrl: string
  Ordem?: number
  Largura?: number | null
  Altura?: number | null
}

type Postagem = {
  CdPostagem: number
  Tipo: string
  Titulo?: string | null
  Descricao?: string | null
  Hash: string
  midias: Midia[]
}

const TINT = '#1abc9c'
const tipoLabel: Record<string, string> = {
  album: 'Álbum do dia',
  promocao: 'Promoção',
  comunicado: 'Comunicado',
  educativo: 'Educativo',
}

function ratioOf(midia: Midia): number | null {
  const w = midia.Largura
  const h = midia.Altura
  if (!w || !h || w <= 0 || h <= 0) return null
  return w / h
}

function sortMidias(midias: Midia[]): Midia[] {
  return [...midias].sort((a, b) => (a.Ordem ?? 0) - (b.Ordem ?? 0))
}

async function fetchMidiaBlob(hash: string, midia: Midia): Promise<Blob> {
  const response = await api.get(`/postagens/${hash}/midias/${midia.CdMidia}/download`, {
    responseType: 'blob',
  })
  return response.data as Blob
}

async function shareMidiaAsFile(blob: Blob, fileName: string, title?: string): Promise<'shared' | 'unsupported'> {
  const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg'
  const file = new File([blob], fileName, { type })

  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    await navigator.share({
      files: [file],
      title: title || 'Foto',
    })
    return 'shared'
  }

  return 'unsupported'
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objectUrl)
}

function MidiaCollage({
  midias,
  variant,
  onOpen,
}: {
  midias: Midia[]
  variant: 'collage' | 'natural'
  onOpen: (index: number) => void
}) {
  const total = midias.length
  if (total === 0) return null

  if (variant === 'natural') {
    return (
      <div className="mt-2 flex flex-col gap-2">
        {midias.map((midia, index) => {
          const ratio = ratioOf(midia) ?? 4 / 3
          return (
            <button
              key={midia.CdMidia}
              type="button"
              onClick={() => onOpen(index)}
              className="block w-full overflow-hidden rounded-xl bg-[#e8eaf1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1abc9c]/40"
              style={{ aspectRatio: String(ratio) }}
            >
              <img src={midia.PublicUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          )
        })}
      </div>
    )
  }

  // collage (álbum) — no web mostra todas as fotos
  if (total === 1) {
    return (
      <button
        type="button"
        onClick={() => onOpen(0)}
        className="mt-2 block h-60 w-full overflow-hidden rounded-xl bg-[#e8eaf1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1abc9c]/40"
      >
        <img src={midias[0].PublicUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
      </button>
    )
  }

  if (total === 3) {
    return (
      <div className="mt-2 grid h-52 grid-cols-2 gap-0.5 overflow-hidden rounded-xl bg-[#e8eaf1]">
        <button type="button" onClick={() => onOpen(0)} className="relative min-h-0 focus:outline-none">
          <img src={midias[0].PublicUrl} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        </button>
        <div className="grid grid-rows-2 gap-0.5">
          {midias.slice(1).map((midia, index) => (
            <button
              key={midia.CdMidia}
              type="button"
              onClick={() => onOpen(index + 1)}
              className="relative min-h-0 focus:outline-none"
            >
              <img src={midia.PublicUrl} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-2 grid grid-cols-2 gap-0.5 overflow-hidden rounded-xl bg-[#e8eaf1]">
      {midias.map((midia, index) => (
        <button
          key={midia.CdMidia}
          type="button"
          onClick={() => onOpen(index)}
          className="relative aspect-square focus:outline-none"
        >
          <img src={midia.PublicUrl} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        </button>
      ))}
    </div>
  )
}

function Lightbox({
  midias,
  index,
  busy,
  onClose,
  onChange,
  onDownload,
  onShare,
}: {
  midias: Midia[]
  index: number
  busy: boolean
  onClose: () => void
  onChange: (index: number) => void
  onDownload: (midia: Midia) => void
  onShare: (midia: Midia) => void
}) {
  const midia = midias[index]
  const total = midias.length
  const touchStartX = useRef<number | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && index > 0) onChange(index - 1)
      if (e.key === 'ArrowRight' && index < total - 1) onChange(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, total, onChange, onClose])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return
    const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current
    touchStartX.current = null
    if (Math.abs(delta) < 50) return
    if (delta > 0 && index > 0) onChange(index - 1)
    if (delta < 0 && index < total - 1) onChange(index + 1)
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/94 text-white" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="min-w-12" />
        <p className="text-[15px] font-semibold">
          {index + 1} / {total}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white/14"
          aria-label="Fechar"
        >
          <FontAwesomeIcon icon={faXmark} className="text-lg" />
        </button>
      </div>

      <div
        className="flex min-h-0 flex-1 items-center justify-center px-2"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img src={midia.PublicUrl} alt="" className="max-h-full max-w-full object-contain select-none" draggable={false} />
      </div>

      <div className="flex items-center justify-between px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2.5">
        <div className="flex min-w-12 justify-start">
          {total > 1 && (
            <button
              type="button"
              disabled={index <= 0 || busy}
              onClick={() => onChange(index - 1)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white/14 disabled:opacity-35"
              aria-label="Foto anterior"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-xl" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            disabled={busy}
            onClick={() => onDownload(midia)}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/14 disabled:opacity-60"
            aria-label={busy ? 'Preparando…' : 'Baixar'}
            aria-busy={busy}
          >
            {busy ? (
              <span
                className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white"
                aria-hidden
              />
            ) : (
              <FontAwesomeIcon icon={faDownload} />
            )}
          </button>
          <button
            type="button"
            onClick={() => onShare(midia)}
            disabled={busy}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/14 disabled:opacity-35"
            aria-label="Compartilhar foto"
          >
            <FontAwesomeIcon icon={faShareNodes} />
          </button>
        </div>

        <div className="flex min-w-12 justify-end">
          {total > 1 && (
            <button
              type="button"
              disabled={index >= total - 1 || busy}
              onClick={() => onChange(index + 1)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white/14 disabled:opacity-35"
              aria-label="Próxima foto"
            >
              <FontAwesomeIcon icon={faChevronRight} className="text-xl" style={{ marginLeft: 2 }} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function PublicPost() {
  const { hash } = useParams<{ hash: string }>()
  const [postagem, setPostagem] = useState<Postagem | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [mediaBusy, setMediaBusy] = useState(false)

  useEffect(() => {
    if (!hash) return
    setLoading(true)
    api
      .get(`/postagens/${hash}`)
      .then((res) => setPostagem(res.data))
      .catch(() => setError('Postagem não encontrada ou expirada.'))
      .finally(() => setLoading(false))
  }, [hash])

  const midias = useMemo(() => sortMidias(postagem?.midias ?? []), [postagem?.midias])
  const shareUrl = useMemo(() => window.location.href, [])
  const pageTitle = postagem?.Titulo || tipoLabel[postagem?.Tipo ?? ''] || 'Postagem'
  const variant = postagem?.Tipo === 'album' ? 'collage' : 'natural'

  const midiaFileName = useCallback(
    (midia: Midia) => `postagem-${postagem?.CdPostagem ?? 'img'}-${midia.CdMidia}.jpg`,
    [postagem?.CdPostagem],
  )

  const onSharePost = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: pageTitle,
          text: postagem?.Descricao || undefined,
          url: shareUrl,
        })
        return
      }
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copiado!')
    } catch {
      // usuário cancelou
    }
  }, [pageTitle, postagem?.Descricao, shareUrl])

  const onDownload = useCallback(
    async (midia: Midia) => {
      if (!hash || mediaBusy) return
      setMediaBusy(true)
      try {
        const blob = await fetchMidiaBlob(hash, midia)
        triggerBlobDownload(blob, midiaFileName(midia))
        toast.success('Download iniciado')
      } catch {
        toast.error('Não foi possível baixar a imagem')
      } finally {
        setMediaBusy(false)
      }
    },
    [hash, mediaBusy, midiaFileName],
  )

  const onShareMidia = useCallback(
    async (midia: Midia) => {
      if (!hash || mediaBusy) return
      setMediaBusy(true)
      try {
        const blob = await fetchMidiaBlob(hash, midia)
        const name = midiaFileName(midia)
        const result = await shareMidiaAsFile(blob, name, pageTitle)
        if (result === 'unsupported') {
          // Desktop / browsers sem share de arquivo: baixa a imagem.
          triggerBlobDownload(blob, name)
          toast.info('Seu navegador não compartilha arquivo — iniciamos o download')
        }
      } catch (err) {
        // AbortError = usuário cancelou o share sheet
        if (err instanceof DOMException && err.name === 'AbortError') return
        toast.error('Não foi possível compartilhar a imagem')
      } finally {
        setMediaBusy(false)
      }
    },
    [hash, mediaBusy, midiaFileName, pageTitle],
  )

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#eef0f8] text-[#8e8e93]">
        <div className="flex flex-col items-center gap-3">
          <div
            className="h-9 w-9 animate-spin rounded-full border-2 border-[#1abc9c]/30 border-t-[#1abc9c]"
            aria-hidden
          />
          <p className="text-sm font-medium">Carregando postagem…</p>
        </div>
      </div>
    )
  }

  if (error || !postagem) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#eef0f8] px-6 text-center">
        <div className="max-w-sm rounded-2xl border border-[#e6e8ef] bg-white p-8 shadow-sm">
          <p className="text-lg font-bold text-[#1c1c1e]">{error || 'Postagem indisponível'}</p>
          <p className="mt-2 text-sm leading-relaxed text-[#8e8e93]">
            O link pode ter expirado ou a postagem não está mais disponível.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#eef0f8] text-[#1c1c1e]">
      <Helmet>
        <title>{pageTitle} · SistemaPET</title>
        <meta name="description" content={postagem.Descricao || pageTitle} />
        {midias[0]?.PublicUrl && <meta property="og:image" content={midias[0].PublicUrl} />}
        <meta property="og:title" content={pageTitle} />
        <meta name="theme-color" content={TINT} />
      </Helmet>

      <div className="mx-auto flex min-h-screen max-w-lg flex-col px-4 py-6 sm:py-10">
        <article className="rounded-2xl border border-[#e6e8ef] bg-white p-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          <div className="mb-2 flex items-center gap-1.5">
            <span className="text-xs font-semibold text-[#1abc9c]">
              {tipoLabel[postagem.Tipo] || postagem.Tipo}
            </span>
          </div>

          {!!postagem.Titulo && (
            <h1 className="mb-1 text-[17px] font-bold leading-snug text-[#1c1c1e]">{postagem.Titulo}</h1>
          )}
          {!!postagem.Descricao && (
            <p className="mb-1 text-sm leading-[21px] text-[#1c1c1e]">{postagem.Descricao}</p>
          )}

          <MidiaCollage midias={midias} variant={variant} onOpen={(index) => setLightbox(index)} />

          <div className="mt-3 border-t border-[#e6e8ef] pt-3">
            <button
              type="button"
              onClick={onSharePost}
              className="flex h-9 w-full items-center justify-center gap-1.5 rounded-full border border-[#d9dee9] bg-white text-[13px] font-semibold text-[#1abc9c] transition hover:bg-[#f7f8fc]"
            >
              <FontAwesomeIcon icon={faShareNodes} className="text-sm" />
              Compartilhar
            </button>
          </div>
        </article>
      </div>

      {lightbox !== null && (
        <Lightbox
          midias={midias}
          index={lightbox}
          busy={mediaBusy}
          onClose={() => setLightbox(null)}
          onChange={setLightbox}
          onDownload={onDownload}
          onShare={onShareMidia}
        />
      )}
    </div>
  )
}
